import { readEntitlementFailure } from '@/lib/entitlement-failure';
/**
 * API 客户端 - 类型定义
 */
import type { Timeline, TranscriptSegment, TaskStatus } from '@/types/editor';

// ============================================
// 基础类型
// ============================================

export interface ApiSuccess<T> {
  success?: true;
  data?: T;
  error?: never;
}

export interface ApiFailure {
  success?: false;
  error?: ApiError;
  data?: never;
}

export interface ApiResponse<T> {
  success?: boolean;
  data?: T;
  error?: ApiError;
}

export interface ApiError {
  httpStatus?: number;
  code: string;
  message: string;
  action?: string;
  actionKey?: string;
  userMessageKey?: string;
  severity?: 'info' | 'warning' | 'error';
  retryable?: boolean;
  requestId?: string;
  details?: Record<string, unknown>;
}

export interface ApiEnvelope<T> {
  success?: boolean;
  data?: T;
  error?: string;
  message?: string;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function formatFastApiValidationLocation(loc: unknown): string {
  if (!Array.isArray(loc)) {
    return '';
  }
  return loc
    .filter((part) => typeof part === 'string' || typeof part === 'number')
    .map(String)
    .filter((part) => part !== 'body' && part !== 'query' && part !== 'path')
    .join('.');
}

function normalizeFastApiValidationError(detail: unknown, fallbackMessage: string): ApiError | null {
  if (!Array.isArray(detail)) {
    return null;
  }

  const issues = detail
    .map((item) => {
      if (!isRecord(item)) {
        return null;
      }
      const location = formatFastApiValidationLocation(item.loc);
      const message = typeof item.msg === 'string' && item.msg.trim()
        ? item.msg.trim()
        : fallbackMessage;
      return location ? `${location}: ${message}` : message;
    })
    .filter((issue): issue is string => Boolean(issue));

  if (issues.length === 0) {
    return null;
  }

  return {
    code: 'VALIDATION_ERROR',
    message: issues.join('; '),
    action: 'fix_fields',
    retryable: false,
    details: { detail },
  };
}

export function normalizeApiError(input: unknown, fallbackMessage = 'Something went wrong'): ApiError {
  const failure = readEntitlementFailure(input);
  if (failure) {
    const source = isRecord(input) && isRecord(input.error) ? input.error : isRecord(input) && isRecord(input.detail) ? input.detail : input;
    return { ...failure, message: isRecord(source) && typeof source.message === 'string' ? source.message : fallbackMessage,
      requestId: isRecord(source) && typeof source.request_id === 'string' ? source.request_id
        : isRecord(source) && typeof source.requestId === 'string' ? source.requestId : undefined };
  }
  if (isRecord(input) && isRecord(input.error)) {
    const error = input.error;
    return {
      code: typeof error.code === 'string' ? error.code : 'UNKNOWN_ERROR',
      message: typeof error.message === 'string' ? error.message : fallbackMessage,
      action: typeof error.action === 'string' ? error.action : undefined,
      actionKey: typeof error.actionKey === 'string' ? error.actionKey : undefined,
      userMessageKey: typeof error.userMessageKey === 'string' ? error.userMessageKey : undefined,
      severity: error.severity === 'info' || error.severity === 'warning' || error.severity === 'error'
        ? error.severity
        : undefined,
      retryable: typeof error.retryable === 'boolean' ? error.retryable : undefined,
      requestId: typeof error.request_id === 'string'
        ? error.request_id
        : typeof error.requestId === 'string'
          ? error.requestId
          : undefined,
      details: isRecord(error.details) ? error.details : undefined,
    };
  }

  if (isRecord(input)) {
    const detail = input.detail;
    const validationError = normalizeFastApiValidationError(detail, fallbackMessage);
    if (validationError) {
      return validationError;
    }

    if (isRecord(detail)) {
      return {
        code: typeof detail.code === 'string' ? detail.code : 'UNKNOWN_ERROR',
        message: typeof detail.message === 'string' ? detail.message : fallbackMessage,
        action: typeof detail.action === 'string' ? detail.action : undefined,
        retryable: typeof detail.retryable === 'boolean' ? detail.retryable : undefined,
        requestId: typeof detail.request_id === 'string' ? detail.request_id : undefined,
        details: detail,
      };
    }

    const message = typeof detail === 'string'
      ? detail
      : typeof input.message === 'string'
        ? input.message
        : typeof input.error === 'string'
          ? input.error
          : fallbackMessage;

    return {
      code: typeof input.code === 'string' ? input.code : 'UNKNOWN_ERROR',
      message,
      details: input,
    };
  }

  return {
    code: 'UNKNOWN_ERROR',
    message: input instanceof Error ? input.message : fallbackMessage,
  };
}

export function normalizeApiResponse<T>(input: unknown, fallbackMessage = 'Something went wrong'): ApiResponse<T> {
  if (isRecord(input) && input.success === false) {
    return { success: false, error: normalizeApiError(input, fallbackMessage) };
  }

  if (isRecord(input) && 'error' in input && !('data' in input)) {
    return { success: false, error: normalizeApiError(input, fallbackMessage) };
  }

  if (isRecord(input) && input.success === true && 'data' in input) {
    return { success: true, data: input.data as T };
  }

  return { success: true, data: input as T };
}

export function unwrapApiResponseData<T>(
  response: ApiResponse<ApiEnvelope<T> | T>,
  fallbackError: ApiError,
): ApiResponse<T> {
  if (response.error) {
    return { error: response.error };
  }

  const payload = response.data;
  if (isRecord(payload) && 'success' in payload) {
    if (payload.success === false || !('data' in payload) || typeof payload.data === 'undefined') {
      const rawMessage = typeof payload.error === 'string'
        ? payload.error
        : typeof payload.message === 'string'
          ? payload.message
          : undefined;
      return {
        error: {
          ...fallbackError,
          details: fallbackError.details ?? (rawMessage ? { rawMessage } : undefined),
        },
      };
    }
    return { data: payload.data as T };
  }

  if (typeof payload === 'undefined') {
    return { error: fallbackError };
  }

  return { data: payload as T };
}

export interface PaginatedResponse<T> {
  items: T[];
  total: number;
  limit: number;
  offset: number;
}

// ============================================
// 资源相关
// ============================================

export interface PresignUploadResponse {
  asset_id: string;
  upload_url: string;
  storage_path: string;
  expires_at: string;
}

export interface ConfirmUploadResponse {
  id: string;
  url: string;
  status: string;
}

export interface WaveformData {
  asset_id: string;
  duration: number;
  sample_rate: number;
  channels: number;
  data: { left: number[]; right?: number[] };
  peaks: { min: number; max: number };
}

// ============================================
// 项目相关
// ============================================

export interface SaveStateResponse {
  success: boolean;
  version: number;
  saved_at: string;
}

export interface VersionConflictError {
  error: 'version_conflict';
  message: string;
  server_version: number;
  suggestion: string;
}

export interface ProjectHistoryItem {
  version: number;
  created_at: string;
  snapshot_type: 'auto' | 'manual' | 'checkpoint';
  description?: string;
}

export interface HistoryVersionDetail {
  version: number;
  timeline: Timeline;
  segments: TranscriptSegment[];
  created_at: string;
}

// ============================================
// 任务相关
// ============================================

export interface TaskStartResponse {
  task_id: string;
  status: string;
}

export interface TaskResultWithData<T = unknown> extends TaskStatus {
  result?: T;
}

// ============================================
// 素材处理相关
// ============================================

export interface ProcessAdditionsStatus {
  task_id: string;
  project_id: string;
  status: 'pending' | 'processing' | 'completed' | 'failed';
  current_step?: string;
  progress: number;
  total_assets: number;
  processed_assets: number;
  created_clips: number;
  error?: string;
}

// ============================================
// 导出相关
// ============================================

export interface ExportJob {
  id: string;
  project_id: string;
  status: 'pending' | 'queued' | 'rendering' | 'uploading' | 'processing' | 'completed' | 'failed' | 'cancelled';
  progress: number;
  output_url?: string;
  output_file_size?: number;
  error_message?: string;
  created_at: string;
  completed_at?: string;
}

export interface ExportStartResponse {
  job_id: string;
  status: string;
}

// ============================================
// 健康检查
// ============================================

export interface HealthCheckResponse {
  status: string;
  timestamp: string;
  version: string;
}
